/**
 * Recurring-plan display helpers (#1315), shared by BillingPage and
 * PaymentSuccessPage so both describe a subscription the same way.
 */

import type { BillingSubscriptionResponse } from './types';
import { formatDate } from './date';

/** Statuses under which Stripe has started the plan (trialing counts). */
const STARTED_STATUSES = new Set(['active', 'trialing']);

/** True once the checkout's subscription is live and granting the plan. */
export function isSubscriptionStarted(sub: BillingSubscriptionResponse | null | undefined): boolean {
  return Boolean(sub && sub.plan !== null && sub.entitled && STARTED_STATUSES.has(sub.status));
}

/**
 * True when the caller holds any subscription the portal can manage,
 * including a past_due one that no longer entitles: that owner needs to
 * update their card, and a new checkout would bill twice.
 */
export function hasSubscription(sub: BillingSubscriptionResponse | null | undefined): boolean {
  return Boolean(sub && sub.status !== 'none' && sub.plan !== null);
}

/**
 * "Monitor plan · Renews Sep 25, 2027". A scheduled cancellation reads
 * "Ends …" instead, and a missing date drops the second half rather than
 * printing "N/A".
 */
export function subscriptionSummary(sub: BillingSubscriptionResponse): string {
  const planName = sub.plan === 'monitor' ? 'Monitor plan' : 'Subscription';
  if (!sub.entitled) return `${planName} · Payment needs attention`;
  const endsAt = sub.cancelAt ?? (sub.status === 'canceled' ? sub.currentPeriodEnd : null);
  if (endsAt) return `${planName} · Ends ${formatDate(endsAt)}`;
  if (sub.currentPeriodEnd) return `${planName} · Renews ${formatDate(sub.currentPeriodEnd)}`;
  return planName;
}

/**
 * Full-page redirect to a Stripe-hosted Checkout or Billing Portal url. An
 * object property rather than a bare function so tests can spy on it (the
 * @swc/jest pattern documented in AGENTS.md).
 */
export const billingRedirect = {
  assign(url: string): void {
    window.location.assign(url);
  },
};
